// src/components/DistrictPropertiesSection.jsx
import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { normalizeMediaUrl } from '../api/base';
import { useCurrencyStore } from '../store/useCurrencyStore';
import DistrictBadge from './DistrictBadge';

const SectionWrap = styled.section`
  margin: 40px 0;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  h2 {
    margin: 0;
    font-size: 24px;
    font-weight: 700;
  }
  
  .view-all {
    color: var(--primary);
    text-decoration: none;
    font-weight: 600;
    padding: 8px 16px;
    border: 1px solid var(--primary);
    border-radius: 8px;
    transition: all 0.2s ease;
    
    &:hover {
      background: var(--primary);
      color: var(--primaryFg);
      transform: translateY(-1px);
    }
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(Link)`
  border: 1px solid var(--bdr);
  border-radius: 16px;
  background: var(--card);
  box-shadow: var(--shadow);
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  display: flex;
  flex-direction: column;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.15);
  }
`;

const CardImage = styled.div`
  width: 100%;
  height: 200px;
  background: #f5f5f5;
  overflow: hidden;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  
  .badge {
    position: absolute;
    top: 12px;
    right: 12px;
  }
`;

const CardContent = styled.div`
  padding: 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
  
  .title {
    font-weight: 700;
    font-size: 16px;
    margin-bottom: 8px;
    line-height: 1.3;
  }
  
  .details {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 12px;
    font-size: 14px;
    color: var(--muted);
  }
  
  .price {
    font-size: 18px;
    font-weight: 900;
    color: var(--primary);
    margin-top: auto;
  }
  
  .developer {
    font-size: 13px;
    color: var(--muted);
    margin-bottom: 8px;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-top: 20px;
  
  .btn {
    padding: 10px 16px;
    border: 1px solid var(--bdr);
    border-radius: 8px;
    background: var(--card);
    color: var(--fg);
    text-decoration: none;
    font-weight: 600;
    transition: all 0.2s ease;
    
    &:hover:not(:disabled) {
      background: var(--primary);
      color: var(--primaryFg);
      transform: translateY(-1px);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
  
  .info {
    color: var(--muted);
    font-size: 14px;
    text-align: center;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: var(--muted);
  border: 1px solid var(--bdr);
  border-radius: 16px;
  background: var(--card);
`;

export default function DistrictPropertiesSection({
  title,
  properties = [],
  loading = false,
  pagination = { page: 1, pages: 1, total: 0 },
  onPageChange,
  viewAllLink,
  language = 'ru',
  emptyMessage
}) {
  const formatPrice = useCurrencyStore(s => s.formatPrice);
  const currencyCode = useCurrencyStore(s => s.currency) || 'AED';

  if (loading) {
    return (
      <SectionWrap>
        <SectionHeader>
          <h2>{title}</h2>
        </SectionHeader>
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--muted)' }}>
          {language === 'en' ? 'Loading...' : 'Загрузка...'}
        </div>
      </SectionWrap>
    );
  }

  if (!properties.length) {
    return (
      <SectionWrap>
        <SectionHeader>
          <h2>{title}</h2>
        </SectionHeader>
        <EmptyState>
          {emptyMessage || (language === 'en' ? 'No properties found' : 'Объекты не найдены')}
        </EmptyState>
      </SectionWrap>
    );
  }

  // Показываем только первые 4 объекта (2 ряда по 2)
  const displayProperties = properties.slice(0, 4);

  return (
    <SectionWrap>
      <SectionHeader>
        <h2>{title}</h2>
        {viewAllLink && properties.length > 4 && (
          <Link to={viewAllLink} className="view-all">
            {language === 'en' ? 'View All' : 'Смотреть все'}
          </Link>
        )}
      </SectionHeader>

      <Grid>
        {displayProperties.map((property, index) => {
          const href = `/realty/${encodeURIComponent(property.slug || property.id || index)}`;
          const cover = normalizeMediaUrl(property.cover_image || property.cover || property.image || property.cover_url || '');
          const title = String(property.title || (language === 'en' ? `Property ${index + 1}` : `Объект ${index + 1}`));
          const price = property.price;
          const curr = (typeof property.currency === 'string' ? property.currency : (property.currency?.code || property.currency?.symbol)) || currencyCode || 'AED';
          
          // Диапазон площади
          const areaRange = property.area_total_range;
          let area = null;
          if (areaRange && (areaRange.min != null || areaRange.max != null)) {
            const aMin = areaRange.min;
            const aMax = areaRange.max;
            area = (aMin != null && aMax != null && aMin !== aMax) ? `${aMin}–${aMax}` : String(aMin != null ? aMin : aMax);
          } else {
            area = property.area_m2 || property.area_total || property.area || null;
          }
          
          // Диапазон спален
          const bedsRange = property.bedrooms_range;
          let beds = null;
          if (bedsRange && (bedsRange.min != null || bedsRange.max != null)) {
            const bMin = bedsRange.min;
            const bMax = bedsRange.max;
            beds = (bMin != null && bMax != null && bMin !== bMax) ? `${bMin}–${bMax}` : (bMin != null ? bMin : bMax);
          } else if (property.bedrooms != null) {
            beds = property.bedrooms;
          }

          const developer = String(property.developer_name_ru || property.developer_name || property.developer || '');

          return (
            <Card key={property.id || property.slug || index} to={href}>
              <CardImage>
                <img 
                  src={cover || 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw=='} 
                  alt={title} 
                  loading="lazy" 
                />
                <div className="badge">
                  <DistrictBadge 
                    slug={property.district_slug} 
                    name={property.district_name} 
                  />
                </div>
              </CardImage>
              
              <CardContent>
                <div className="title">{title}</div>
                
                {developer && (
                  <div className="developer">{developer}</div>
                )}
                
                <div className="details">
                  {beds != null && (
                    <span>
                      {(beds === 0 || beds === '0') 
                        ? (language === 'en' ? 'Studio' : 'Студия') 
                        : `${beds} ${language === 'en' ? 'br' : 'спальни'}`}
                    </span>
                  )}
                  {area != null && (
                    <span>• {area} {language === 'en' ? 'm²' : 'м²'}</span>
                  )}
                </div>
                
                {price != null && (
                  <div 
                    className="price"
                    dangerouslySetInnerHTML={{
                      __html: formatPrice(price)
                    }}
                  />
                )}
              </CardContent>
            </Card>
          );
        })}
      </Grid>

      {pagination.pages > 1 && onPageChange && (
        <Pagination>
          <button
            className="btn"
            disabled={pagination.page <= 1}
            onClick={() => onPageChange(Math.max(1, pagination.page - 1))}
          >
            ← {language === 'en' ? 'Previous' : 'Назад'}
          </button>

          <div className="info">
            {language === 'en'
              ? `Page ${pagination.page} of ${pagination.pages} • ${pagination.total} total`
              : `Стр. ${pagination.page} из ${pagination.pages} • всего ${pagination.total}`}
          </div>

          <button
            className="btn"
            disabled={pagination.page >= pagination.pages}
            onClick={() => onPageChange(Math.min(pagination.pages, pagination.page + 1))}
          >
            {language === 'en' ? 'Next' : 'Вперёд'} →
          </button>
        </Pagination>
      )}
    </SectionWrap>
  );
}